import { BOW } from "@eldrun/content";
import type { ArrowEndEvent, ArrowEvent } from "@eldrun/protocol";
import { ballisticAt, type Ballistic } from "@eldrun/sim";
import { BoxGeometry, ConeGeometry, Group, Mesh, Quaternion, Vector3, type Object3D } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { toonMaterial } from "../render/shaders/toon.ts";
import { unindexed } from "../render/geometry/unindexed.ts";
import { RibbonPool, type Ribbon, type RibbonLook } from "./ribbons.ts";

interface Flying {
  key: string;
  owner: string;
  path: Ballistic;
  born: number;
  mesh: Group;
  /** A fita atrás dela (F92), se coube no teto. */
  ribbon: Ribbon | undefined;
}

interface Stuck {
  mesh: Group;
  until: number;
}

const FORWARD = new Vector3(0, 0, -1);
const UP = new Vector3(0, 1, 0);
const q = new Quaternion();
const v = new Vector3();
const side = new Vector3();
const toEye = new Vector3();
const at = { x: 0, y: 0, z: 0 };

/** Fitas atrás das flechas em voo ao mesmo tempo (F92); as demais voam sem. */
export const ARROW_RIBBONS = 8;
/** Da posição da flecha (a ponta) até o fim da haste, e a meia largura da fita (m). */
export const NOCK = 0.65;
const RIBBON_HALF = 0.06;

type Point = { readonly x: number; readonly y: number; readonly z: number };

/**
 * A amostra da fita (F92) da flecha com a ponta em `p`, voando em `dir` (unitário), que já andou `flown` m
 * desde a boca do arco: atrás da haste, virada para o olho em `eye` (os lados de través ao voo e à linha do
 * olho; deitada, quem atira e quem está na altura dela a via de lado). A fita começa no fim da haste, mas
 * não antes da boca do arco (`BOW.muzzle`, à frente do olho de quem atira). Com o olho na linha do voo, os
 * lados ficam na horizontal (ou em x, no voo na vertical).
 */
export function sampleArrowRibbon(ribbon: Ribbon, now: number, p: Point, dir: Vector3, flown: number, eye: Point): void {
  const back = Math.min(NOCK, flown);
  const nx = p.x - dir.x * back;
  const ny = p.y - dir.y * back;
  const nz = p.z - dir.z * back;
  toEye.set(eye.x - nx, eye.y - ny, eye.z - nz);
  side.crossVectors(dir, toEye);
  if (side.lengthSq() < 1e-6 * Math.max(1e-6, toEye.lengthSq())) side.crossVectors(dir, UP);
  if (side.lengthSq() < 1e-6) side.set(1, 0, 0);
  side.normalize().multiplyScalar(RIBBON_HALF);
  ribbon.sample(now, nx - side.x, ny - side.y, nz - side.z, nx + side.x, ny + side.y, nz + side.z);
}

/** Chave da flecha: as do próprio jogador casam pelo disparo (previstas antes do evento). */
export const arrowKey = (owner: string, shot: number, index: number): string => `${owner}:${shot}:${index}`;

/**
 * Flechas no mundo (doc 02 §12): em voo pela mesma curva balística do servidor (`libs/sim`), as do
 * próprio jogador previstas na hora e rebaseadas pelo evento; cravadas por 8 s onde o servidor disse.
 */
export class ArrowLayer {
  private readonly root: Object3D;
  // Malha neon (F59): primitivas sem índice.
  private readonly shaft = unindexed(new BoxGeometry(0.03, 0.03, 0.7));
  private readonly head = unindexed(new ConeGeometry(0.035, 0.1, 4));
  private readonly wood;
  private readonly tip;
  private readonly flying = new Map<string, Flying>();
  private readonly stuck: Stuck[] = [];
  private readonly ribbons: RibbonPool;
  /** O visual da fita de cada atirador (a cor da classe dele), ou nenhuma fita. */
  private readonly ribbonLook: (owner: string) => RibbonLook | undefined;

  constructor(root: Object3D, atmosphere: AtmosphereUniforms, ribbonLook: (owner: string) => RibbonLook | undefined = () => undefined) {
    this.root = root;
    this.ribbons = new RibbonPool(root, ARROW_RIBBONS);
    this.ribbonLook = ribbonLook;
    // Item (a flecha): na máscara do contorno (F51).
    this.wood = toonMaterial(atmosphere, "#8A6A45", 0.6, false, { outline: true });
    this.tip = toonMaterial(atmosphere, "#9AA4B0", 0.8, false, { outline: true });
  }

  private model(): Group {
    const g = new Group();
    const shaft = new Mesh(this.shaft, this.wood);
    shaft.position.z = 0.3;
    const head = new Mesh(this.head, this.tip);
    head.rotation.x = -Math.PI / 2;
    head.position.z = -0.08;
    g.add(shaft, head);
    this.root.add(g);
    return g;
  }

  /**
   * Flecha prevista (própria) ou vinda do servidor; `born` é o instante local do disparo (ms). Se
   * já existe (a prevista), passa a seguir a curva e o instante do servidor.
   */
  launch(key: string, owner: string, path: Ballistic, born: number): void {
    const existing = this.flying.get(key);
    if (existing) {
      existing.path = path;
      existing.born = born;
      return;
    }
    const look = this.ribbonLook(owner);
    const ribbon = look && look.alpha > 0 ? this.ribbons.acquire(look) : undefined;
    this.flying.set(key, { key, owner, path, born, mesh: this.model(), ribbon });
  }

  /**
   * Evento do servidor: o disparo aconteceu lá cerca de RTT/2 antes de o evento chegar, então a
   * flecha já tem essa idade (as remotas não começam do zero; a própria é rebaseada na curva dele).
   */
  fromEvent(e: ArrowEvent, now: number, rtt: number): void {
    this.launch(arrowKey(e.owner, e.shot, e.index), e.owner, { x: e.x, y: e.y, z: e.z, vx: e.vx, vy: e.vy, vz: e.vz, gravity: BOW.gravity }, now - rtt / 2);
  }

  /** O servidor recusou o disparo: as flechas previstas dele somem. */
  cancelShot(owner: string, shot: number): void {
    const prefix = `${owner}:${shot}:`;
    for (const [key, f] of this.flying) {
      if (!key.startsWith(prefix)) continue;
      this.root.remove(f.mesh);
      f.ribbon?.release(performance.now());
      this.flying.delete(key);
    }
  }

  /** O servidor encerrou a flecha: some do voo e, se cravou, fica parada 8 s no ponto. */
  end(e: ArrowEndEvent, now: number): void {
    const key = arrowKey(e.owner, e.shot, e.index);
    const f = this.flying.get(key);
    if (!f) return;
    this.flying.delete(key);
    // A fita encolhe até onde a flecha estava e some.
    f.ribbon?.release(now);
    if (!e.stuck) {
      this.root.remove(f.mesh);
      return;
    }
    f.mesh.position.set(e.x, e.y, e.z);
    this.stuck.push({ mesh: f.mesh, until: now + BOW.stuckSeconds * 1000 });
  }

  /** `eye`: a posição da câmera, para onde as fitas viram. */
  update(now: number, eye: Point): void {
    for (const f of this.flying.values()) {
      const t = (now - f.born) / 1000;
      if (t > BOW.life + 1) {
        // Sem o fim do servidor (pacote perdido): some sozinha.
        this.root.remove(f.mesh);
        f.ribbon?.release(now);
        this.flying.delete(f.key);
        continue;
      }
      const p = ballisticAt(f.path, t, at);
      f.mesh.position.set(p.x, p.y, p.z);
      f.mesh.quaternion.copy(q.setFromUnitVectors(FORWARD, v.set(f.path.vx, f.path.vy - f.path.gravity * t, f.path.vz).normalize()));
      if (f.ribbon) sampleArrowRibbon(f.ribbon, now, p, v, Math.hypot(p.x - f.path.x, p.y - f.path.y, p.z - f.path.z), eye);
    }
    this.ribbons.update(now);
    for (let i = this.stuck.length - 1; i >= 0; i--) {
      const s = this.stuck[i] as Stuck;
      if (now < s.until) continue;
      this.root.remove(s.mesh);
      this.stuck.splice(i, 1);
    }
  }

  get stats(): { flying: number; stuck: number; ribbons: { active: number; fading: number; visible: number; capacity: number } } {
    return { flying: this.flying.size, stuck: this.stuck.length, ribbons: this.ribbons.stats };
  }

  dispose(): void {
    for (const f of this.flying.values()) this.root.remove(f.mesh);
    for (const s of this.stuck) this.root.remove(s.mesh);
    this.flying.clear();
    this.stuck.length = 0;
    this.ribbons.dispose();
    this.shaft.dispose();
    this.head.dispose();
  }
}
